import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Input, InputNumber, Select, Space, Tag, PaginationProps,
  Card, Button, Drawer, Descriptions, Tabs, Badge,
  Modal, Form, message, Popconfirm, Spin, Empty,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, EyeOutlined,
  EditOutlined, DeleteOutlined, MobileOutlined, GlobalOutlined, AppleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import adminService, {
  AdminUserItem, AdminUserDetail, AdminUserListing,
} from '../../api/services/adminService';
import { UserCreditSummary } from '../../api/services/adminService';
import { UserRole, UserStatus } from '@enum';

const useRoleOptions = () => {
  const { t } = useTranslation();
  return [
    { label: t('admin.common.all'), value: '' },
    { label: UserRole.PRIVATE, value: UserRole.PRIVATE },
    { label: UserRole.DEALER, value: UserRole.DEALER },
    { label: UserRole.ADMIN, value: UserRole.ADMIN },
    { label: UserRole.SHOP, value: UserRole.SHOP },
    { label: UserRole.USER, value: UserRole.USER },
    { label: UserRole.SUPER_ADMIN, value: UserRole.SUPER_ADMIN },
  ];
};

const useStatusOptions = () => {
  const { t } = useTranslation();
  return [
    { label: t('admin.common.all'), value: '' },
    { label: UserStatus.ACTIVE, value: UserStatus.ACTIVE },
    { label: UserStatus.DISABLED, value: UserStatus.DISABLED },
    { label: UserStatus.PENDING_VERIFICATION, value: UserStatus.PENDING_VERIFICATION },
    { label: UserStatus.SUSPENDED, value: UserStatus.SUSPENDED },
  ];
};

const formatDate = (iso: string) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const DeviceIcon = ({ type }: { type?: string | null }) => {
  if (!type) return <span style={{ color: '#bbb' }}>—</span>;
  if (type === 'web') return <Tag icon={<GlobalOutlined />} color="blue">Web</Tag>;
  if (type === 'apple') return <Tag icon={<AppleOutlined />} color="default">Apple</Tag>;
  if (type === 'android') return <Tag icon={<MobileOutlined />} color="green">Android</Tag>;
  return <Tag>{type}</Tag>;
};

const SignupMethodBadge = ({ method }: { method?: string | null }) => {
  if (!method) return <span style={{ color: '#bbb' }}>—</span>;
  if (method === 'google') return (
    <Tag style={{ border: 'none', background: '#fff3e0', color: '#e65100' }}>
      <img src="https://www.svgrepo.com/show/475656/google-color.svg" width={12} style={{ marginRight: 4, verticalAlign: 'middle' }} alt="" />
      Google
    </Tag>
  );
  if (method === 'apple') return <Tag icon={<AppleOutlined />} color="default">Apple</Tag>;
  if (method === 'email') return <Tag icon={<span style={{ marginRight: 4 }}>✉</span>} color="cyan">Email</Tag>;
  return <Tag>{method}</Tag>;
};

// ─── User Detail Drawer ────────────────────────────────────────────────────
const UserDetailDrawer: React.FC<{
  userId: string | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onUpdated: (user: AdminUserItem) => void;
}> = ({ userId, onClose, onDeleted, onUpdated }) => {
  const { t } = useTranslation();
  const roleOptions = useRoleOptions();
  const statusOptions = useStatusOptions();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [listings, setListings] = useState<AdminUserListing[]>([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [listingsPage, setListingsPage] = useState(1);
  const [creditSummary, setCreditSummary] = useState<UserCreditSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [creditVisible, setCreditVisible] = useState(false);
  const [creditForm] = Form.useForm();
  const [creditSaving, setCreditSaving] = useState(false);

  useEffect(() => {
    if (!userId) { setUser(null); return; }
    setLoading(true);
    setListingsPage(1);
    Promise.all([
      adminService.getUser(userId),
      adminService.getUserListings(userId, 1, 10),
      adminService.getUserCreditSummary(userId),
    ]).then(([u, l, c]) => {
      setUser(u);
      setListings(l.listings);
      setListingsTotal(l.total);
      setCreditSummary(c);
    }).catch(() => message.error(t('admin.users.msg_load_failed')))
      .finally(() => setLoading(false));
  }, [userId]);

  const loadListings = async (page: number) => {
    if (!userId) return;
    setListingsLoading(true);
    try {
      const res = await adminService.getUserListings(userId, page, 10);
      setListings(res.listings);
      setListingsTotal(res.total);
      setListingsPage(page);
    } finally {
      setListingsLoading(false);
    }
  };

  const openEdit = () => {
    editForm.setFieldsValue({
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      phone: (user?.phone || '').replace(/^\+355/, ''),
      role: user?.role,
      status: user?.status,
    });
    setEditVisible(true);
  };

  const handleSave = async () => {
    const values = await editForm.validateFields();
    setSaving(true);
    try {
      const updated = await adminService.updateUser(userId!, values);
      setUser(prev => prev ? { ...prev, ...updated } : null);
      onUpdated(updated as any);
      setEditVisible(false);
      message.success(t('admin.users.msg_updated'));
    } catch {
      message.error(t('admin.users.msg_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCredit = async () => {
    const { amount, reason } = await creditForm.validateFields();
    setCreditSaving(true);
    try {
      const res = await adminService.adjustUserCredits(userId!, amount, reason);
      setUser(prev => prev ? { ...prev, creditBalance: res.creditBalance } : null);
      adminService.getUserCreditSummary(userId!).then(setCreditSummary).catch(() => {});
      onUpdated({ id: userId!, creditBalance: res.creditBalance } as any);
      setCreditVisible(false);
      creditForm.resetFields();
      message.success(amount > 0 ? t('admin.users.msg_credits_added') : t('admin.users.msg_credits_deducted'));
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('admin.users.msg_credits_failed'));
    } finally {
      setCreditSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminService.deleteUser(userId!);
      message.success(t('admin.users.msg_deleted'));
      onDeleted(userId!);
      onClose();
    } catch {
      message.error(t('admin.users.msg_delete_failed'));
    }
  };

  const listingColumns = [
    {
      title: t('admin.users.col_photo'),
      key: 'photo',
      width: 60,
      render: (_: any, r: AdminUserListing) =>
        r.thumbnailUrl || r.image ? (
          <img src={r.thumbnailUrl || r.image || undefined} alt="" style={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 4 }} />
        ) : <div style={{ width: 48, height: 36, background: '#f0f0f0', borderRadius: 4 }} />,
    },
    {
      title: t('admin.users.col_vehicle'),
      key: 'vehicle',
      render: (_: any, r: AdminUserListing) => `${r.year} ${r.make} ${r.model}`,
    },
    {
      title: t('admin.users.col_price'),
      dataIndex: 'price',
      key: 'price',
      render: (v: number) => `${v?.toLocaleString() ?? '-'} ALL`,
    },
    {
      title: t('admin.common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        <Tag color={v === 'ACTIVE' ? 'success' : v === 'PENDING' ? 'warning' : 'default'}>{v}</Tag>
      ),
    },
    {
      title: t('admin.users.col_created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDate,
    },
  ];

  const txColumns = [
    {
      title: t('admin.common.date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDate,
    },
    {
      title: t('admin.users.col_type'),
      dataIndex: 'type',
      key: 'type',
      render: (v: string) => {
        const colors: Record<string, string> = {
          PURCHASE: 'blue', SUBSCRIPTION_GRANT: 'green',
          AD_POST: 'orange', ADMIN_ADJUSTMENT: 'purple', REFUND: 'red',
        };
        return <Tag color={colors[v] ?? 'default'}>{v}</Tag>;
      },
    },
    {
      title: t('admin.users.col_amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (v: number) => (
        <span style={{ color: v > 0 ? '#52c41a' : '#f5222d', fontWeight: 600 }}>
          {v > 0 ? `+${v}` : v}
        </span>
      ),
    },
    {
      title: t('admin.users.col_balance_after'),
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (v: number) => v ?? '-',
    },
    { title: t('admin.users.col_reason'), dataIndex: 'reason', key: 'reason' },
  ];

  return (
    <>
      <Drawer
        title={user ? `${user.firstName} ${user.lastName}` : t('admin.users.drawer_title_fallback')}
        width={820}
        open={!!userId}
        onClose={onClose}
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={openEdit}>{t('admin.users.btn_edit')}</Button>
            <Button type="primary" onClick={() => { creditForm.resetFields(); setCreditVisible(true); }}>
              {t('admin.users.btn_add_credits')}
            </Button>
            <Popconfirm
              title={t('admin.users.delete_confirm_title')}
              description={t('admin.users.delete_confirm_desc')}
              onConfirm={handleDelete}
              okText={t('admin.users.btn_delete')}
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>{t('admin.users.btn_delete')}</Button>
            </Popconfirm>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin size="large" /></div>
        ) : !user ? null : (
          <Tabs
            defaultActiveKey="info"
            items={[
              {
                key: 'info',
                label: t('admin.users.tab_profile'),
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label={t('admin.users.label_first_name')}>{user.firstName || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_last_name')}>{user.lastName || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_email')}>{user.email}</Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_phone')}>{user.phone || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_role')}>
                      <Tag color="processing">{user.role}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_status')}>
                      <Tag color={user.status === 'ACTIVE' ? 'success' : 'warning'}>{user.status}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_joined_via')}><SignupMethodBadge method={user.signupMethod} /></Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_device')}><DeviceIcon type={user.deviceType} /></Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_credit_balance')}>
                      <Badge count={user.creditBalance ?? 0} showZero color="#1890ff" />
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_email_verified')}>
                      <Tag color={user.emailVerified ? 'success' : 'error'}>{user.emailVerified ? t('admin.common.yes') : t('admin.common.no')}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_active')}>
                      <Tag color={user.isActive ? 'success' : 'error'}>{user.isActive ? t('admin.common.yes') : t('admin.common.no')}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_last_login')}>{user.lastLogin ? formatDate(user.lastLogin) : '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('admin.users.label_joined')}>{formatDate(user.createdAt)}</Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: 'listings',
                label: t('admin.users.tab_listings', { count: listingsTotal }),
                children: listingsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : listings.length === 0 ? (
                  <Empty description={t('admin.users.no_listings')} />
                ) : (
                  <Table
                    rowKey="id"
                    size="small"
                    columns={listingColumns}
                    dataSource={listings}
                    pagination={{
                      current: listingsPage,
                      pageSize: 10,
                      total: listingsTotal,
                      onChange: loadListings,
                    }}
                  />
                ),
              },
              {
                key: 'transactions',
                label: t('admin.users.tab_transactions', { count: creditSummary?.summary.totalTransactions ?? 0 }),
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      {[
                        { label: t('admin.users.balance_label'), value: creditSummary?.user.currentBalance ?? 0, color: '#1890ff' },
                        { label: t('admin.users.credits_in'), value: creditSummary?.summary.totalCreditsReceived ?? 0, color: '#52c41a' },
                        { label: t('admin.users.credits_out'), value: creditSummary?.summary.totalCreditsUsed ?? 0, color: '#f5222d' },
                      ].map(s => (
                        <Card key={s.label} size="small" style={{ flex: 1, textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                          <div style={{ color: '#888', fontSize: 12 }}>{s.label}</div>
                        </Card>
                      ))}
                    </div>
                    {creditLoading ? <Spin /> : (
                      <Table
                        rowKey="id"
                        size="small"
                        columns={txColumns}
                        dataSource={creditSummary?.transactions ?? []}
                        pagination={{ pageSize: 10 }}
                      />
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      {/* Add Credits Modal */}
      <Modal
        title={t('admin.users.adjust_credits_title', { name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() })}
        open={creditVisible}
        onCancel={() => setCreditVisible(false)}
        onOk={handleCredit}
        confirmLoading={creditSaving}
        okText={t('admin.users.btn_confirm')}
      >
        <p style={{ color: '#888', marginBottom: 16 }}>
          {t('admin.users.current_balance')} <strong style={{ color: '#1890ff' }}>{user?.creditBalance ?? 0}</strong> {t('admin.users.credits_unit')}
        </p>
        <Form form={creditForm} layout="vertical">
          <Form.Item
            name="amount"
            label={t('admin.users.amount_label')}
            rules={[
              { required: true, message: t('admin.users.amount_required') },
              { type: 'number', min: -99999, max: 99999, message: t('admin.users.amount_range') },
              {
                validator: (_, v) => v === 0 ? Promise.reject(t('admin.users.amount_not_zero')) : Promise.resolve(),
              },
            ]}
            extra={t('admin.users.amount_extra')}
          >
            <InputNumber style={{ width: '100%' }} placeholder={t('admin.users.amount_placeholder')} />
          </Form.Item>
          <Form.Item name="reason" label={t('admin.users.reason_label')}>
            <Input placeholder={t('admin.users.reason_placeholder')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t('admin.users.edit_user_title')}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={t('admin.users.btn_save')}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="firstName" label={t('admin.users.label_first_name')}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label={t('admin.users.label_last_name')}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t('admin.users.label_email')} rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t('admin.users.label_phone')}>
            <Input addonBefore="+355" />
          </Form.Item>
          <Form.Item name="role" label={t('admin.users.label_role')}>
            <Select options={roleOptions.filter(o => o.value !== '')} />
          </Form.Item>
          <Form.Item name="status" label={t('admin.users.label_status')}>
            <Select options={statusOptions.filter(o => o.value !== '')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const AdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const roleOptions = useRoleOptions();
  const statusOptions = useStatusOptions();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  type RoleFilter = '' | AdminUserItem['role'];
  type StatusFilter = '' | AdminUserItem['status'];
  const [role, setRole] = useState<RoleFilter>('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [search, setSearch] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminService.listUsers({
        role: role === '' ? undefined : role,
        status: status === '' ? undefined : status,
        search,
        page,
        pageSize,
      });
      setUsers(res.data || []);
      setTotal(res.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, pageSize]);
  useEffect(() => { setPage(1); fetchData(); }, [role, status]);
  useEffect(() => {
    const handle = setTimeout(() => { setPage(1); fetchData(); }, 400);
    return () => clearTimeout(handle);
  }, [search]);

  const columns = useMemo(() => [
    { title: t('admin.users.col_email'), dataIndex: 'email', key: 'email' },
    {
      title: t('admin.users.col_name'),
      key: 'name',
      render: (_: any, r: AdminUserItem) =>
        `${r.firstName || ''} ${r.lastName || ''}`.trim() || '-',
    },
    { title: t('admin.users.col_phone'), dataIndex: 'phone', key: 'phone', render: (v: string) => v || '-' },
    {
      title: t('admin.users.col_role'),
      dataIndex: 'role',
      key: 'role',
      render: (v: string) => (
        <Tag color="processing" style={{ border: 'none', background: 'rgba(24,144,255,0.1)', color: '#1890ff' }}>{v}</Tag>
      ),
    },
    {
      title: t('admin.users.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        v === 'ACTIVE'
          ? <Tag color="success" style={{ border: 'none', background: 'rgba(82,196,26,0.1)', color: '#52c41a' }}>{v}</Tag>
          : <Tag className="ant-tag-orange">{v}</Tag>
      ),
    },
    {
      title: t('admin.users.col_joined_via'),
      dataIndex: 'signupMethod',
      key: 'signupMethod',
      render: (v: string) => <SignupMethodBadge method={v} />,
    },
    {
      title: t('admin.users.col_device'),
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (v: string) => <DeviceIcon type={v} />,
    },
    {
      title: t('admin.users.col_balance'),
      dataIndex: 'creditBalance',
      key: 'creditBalance',
      render: (v: number) => <Badge count={v ?? 0} showZero color="#1890ff" />,
    },
    {
      title: t('admin.users.col_joined'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: formatDate,
    },
    {
      title: t('admin.users.col_actions'),
      key: 'actions',
      render: (_: any, r: AdminUserItem) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setSelectedUserId(r.id)}
        >
          {t('admin.users.btn_view')}
        </Button>
      ),
    },
  ], [t]);

  const onChangePage: PaginationProps['onChange'] = (p, ps) => {
    setPage(p);
    setPageSize(ps || 20);
  };

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Card bordered={false} className="admin-filter-card">
          <Space wrap>
            <Input
              placeholder={t('admin.users.search_placeholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onPressEnter={() => { setPage(1); fetchData(); }}
              allowClear
              className="admin-filter-input"
              prefix={<SearchOutlined />}
              style={{ width: 320 }}
            />
            <Select
              style={{ width: 200 }}
              value={role}
              className="admin-filter-select"
              onChange={setRole}
              options={roleOptions}
              placeholder={t('admin.users.role_placeholder')}
            />
            <Select
              style={{ width: 240 }}
              value={status}
              className="admin-filter-select"
              onChange={setStatus}
              options={statusOptions}
              placeholder={t('admin.users.status_placeholder')}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); fetchData(); }} className="admin-filter-btn">
              {t('admin.common.refresh')}
            </Button>
          </Space>
        </Card>

        <Card
          title={<span style={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>{t('admin.users.all_users_title', { count: total })}</span>}
          className="admin-table-card"
          bordered={false}
        >
          <Table
            className="admin-table"
            rowKey={r => r.id}
            loading={loading}
            columns={columns as any}
            dataSource={users}
            pagination={{
              current: page,
              pageSize,
              total,
              onChange: onChangePage,
              showSizeChanger: true,
            }}
          />
        </Card>
      </Space>

      <UserDetailDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onDeleted={id => setUsers(prev => prev.filter(u => u.id !== id))}
        onUpdated={updated => setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u))}
      />
    </>
  );
};

export default AdminUsers;
